import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useSnackbar } from 'notistack';
import { AuthContext } from '../context/AuthContext';
import { Box, CircularProgress, Typography, Paper, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Grid } from '@mui/material';
import { FaPlus, FaEye, FaEdit, FaTrash } from 'react-icons/fa';
import CloseIcon from '@mui/icons-material/Close';

import { getDestekTalepleri, addDestekTalep, deleteDestekTalep, getDestekTalepById, updateDestekTalep, getDestekDurumlari } from '../services/api';
import YeniDestekTalepForm from '../components/YeniDestekTalepForm';


const themeColor = '#F97316';
const themeColorDark = '#EA580C';

const DestekTalepleri = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const { enqueueSnackbar } = useSnackbar();
    const { token } = useContext(AuthContext);

    const [isNewOrEditModalOpen, setNewOrEditModalOpen] = useState(false);
    const [isViewModalOpen, setViewModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [destekDurumlari, setDestekDurumlari] = useState([]);
    
    const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 1)).toISOString().slice(0, 10));
    const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));

    const handleListele = useCallback(async () => {
        setLoading(true);
        try {
            const filterData = { TARIH1: startDate.split('-').reverse().join('.'), TARIH2: endDate.split('-').reverse().join('.') };
            const response = await getDestekTalepleri(filterData);
            setTickets(response.data.data || response.data.Data || []);
        } catch (err) {
            enqueueSnackbar(`Destek talepleri alınamadı: ${err.message}`, { variant: 'error' });
            setTickets([]);
        } finally { setLoading(false); }
    }, [startDate, endDate, enqueueSnackbar]);

    useEffect(() => {
        if (token) {
            handleListele();
            const fetchDurumlar = async () => {
                try {
                    const response = await getDestekDurumlari();
                    setDestekDurumlari(response.data.data || []);
                } catch (error) {
                    enqueueSnackbar('Destek durumları alınamadı.', { variant: 'error' });
                }
            };
            fetchDurumlar();
        }
    }, [token]); 

    const handleOpenModal = async (ticketId = null, mode = 'new') => {
        if (mode === 'new') {
            setSelectedTicket(null);
            setNewOrEditModalOpen(true);
        } else {
            setModalLoading(true);
            setNewOrEditModalOpen(mode === 'edit');
            setViewModalOpen(mode === 'view');
            try {
                const response = await getDestekTalepById(ticketId);
                const ticketData = (response.data.data && response.data.data[0]) || null;
                setSelectedTicket(ticketData);
            } catch (error) {
                enqueueSnackbar(`Talep detayı alınamadı: ${error.message}`, { variant: 'error' });
                closeModals();
            } finally { setModalLoading(false); }
        }
    };
    
    const handleFormSubmit = async (formData) => {
        const isEditing = !!selectedTicket;
        let ticketData;

        if (isEditing) {
            const { ZAMAN, ...restOfTicket } = selectedTicket;
            const formattedDate = new Date(ZAMAN.year, ZAMAN.month - 1, ZAMAN.day).toLocaleDateString('tr-TR');
            ticketData = { ...restOfTicket, ...formData, ZAMAN2: formattedDate };
        } else {
            ticketData = { ...formData, DESTEK_DURUM_ID: 1, ZAMAN2: new Date().toLocaleDateString('tr-TR') };
        }
        
        try {
            if (isEditing) {
                await updateDestekTalep(selectedTicket.DESTEK_ID, ticketData);
                enqueueSnackbar('Talep başarıyla güncellendi!', { variant: 'success' });
            } else {
                await addDestekTalep(ticketData);
                enqueueSnackbar('Destek talebiniz başarıyla oluşturuldu!', { variant: 'success' });
            }
            closeModals();
            handleListele();
        } catch (err) {
            enqueueSnackbar(`İşlem başarısız: ${err.message}`, { variant: 'error' });
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Bu destek talebini silmek istediğinizden emin misiniz?')) {
            try {
                await deleteDestekTalep(id);
                enqueueSnackbar('Talep başarıyla silindi.', { variant: 'success' });
                handleListele();
            } catch (err) {
                enqueueSnackbar(`Silme işlemi başarısız: ${err.message}`, { variant: 'error' });
            }
        }
    };

    const formatDate = (dateInput) => {
        if (!dateInput) return '-';
        let date;
        if (typeof dateInput === 'object' && dateInput.year) {
            date = new Date(dateInput.year, dateInput.month - 1, dateInput.day || dateInput.dayOfMonth);
        } else if (typeof dateInput === 'string') { date = new Date(dateInput); } 
        else { return '-' }
        if (isNaN(date.getTime())) { return 'Geçersiz Tarih'; }
        return new Date(date).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    };

    const closeModals = () => {
        setNewOrEditModalOpen(false);
        setViewModalOpen(false);
        setSelectedTicket(null);
    };

    return (
        <>
            <Paper sx={{ p: { xs: 1, sm: 2 }, mt: 2, borderRadius: 2 }}>
                <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>Destek Taleplerim</Typography>
                
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', my: 2 }}>
                    <TextField label="Başlangıç Tarihi" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                    <TextField label="Bitiş Tarihi" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
                    <Button variant="contained" onClick={handleListele} disabled={loading} sx={{ backgroundColor: themeColor, '&:hover': { backgroundColor: themeColorDark } }}>Filtrele</Button>
                    <Button variant="contained" startIcon={<FaPlus />} onClick={() => handleOpenModal(null, 'new')} sx={{ backgroundColor: themeColor, '&:hover': { backgroundColor: themeColorDark }, ml: 'auto' }}>Yeni Destek Talebi</Button>
                </Box>

                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ backgroundColor: themeColor }}>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Tarih</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Destek No</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Durum</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Başlık</TableCell>
                                <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="center">İşlemler</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? <TableRow><TableCell colSpan={5} align="center"><CircularProgress sx={{ color: themeColor, my: 4 }} /></TableCell></TableRow>
                            : tickets.length > 0 ? tickets.map((ticket) => (
                                <TableRow key={ticket.DESTEK_ID} hover>
                                    <TableCell>{formatDate(ticket.ZAMAN)}</TableCell>
                                    <TableCell>#{ticket.DESTEK_ID}</TableCell>
                                    <TableCell><Box component="span" sx={{ padding: '4px 8px', borderRadius: '12px', fontWeight: 'bold', color: ticket.DESTEK_DURUM_ADI === 'Kapandı' ? 'grey' : 'green', backgroundColor: ticket.DESTEK_DURUM_ADI === 'Kapandı' ? 'rgba(128, 128, 128, 0.1)' : 'rgba(46, 204, 113, 0.1)' }}>{ticket.DESTEK_DURUM_ADI}</Box></TableCell>
                                    <TableCell>{ticket.DESTEK_BASLIK}</TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => handleOpenModal(ticket.DESTEK_ID, 'view')} sx={{ color: themeColor }}><FaEye /></IconButton>
                                        <IconButton size="small" onClick={() => handleOpenModal(ticket.DESTEK_ID, 'edit')} sx={{ color: 'primary.main' }}><FaEdit /></IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(ticket.DESTEK_ID)}><FaTrash /></IconButton>
                                    </TableCell>
                                </TableRow>
                            )) : <TableRow><TableCell colSpan={5} align="center">Filtreye uygun destek talebi bulunamadı.</TableCell></TableRow>}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={isViewModalOpen} onClose={closeModals} fullWidth maxWidth="sm">
                <DialogTitle>Talep Detayları <IconButton onClick={closeModals} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton></DialogTitle>
                <DialogContent dividers>
                    {modalLoading ? <CircularProgress sx={{color: themeColor}} /> : selectedTicket && (
                        <Grid container spacing={2} sx={{p:1}}>
                            <Grid item xs={6}><Typography variant="caption" color="text.secondary">Destek No</Typography><Typography>#{selectedTicket.DESTEK_ID}</Typography></Grid>
                            <Grid item xs={6}><Typography variant="caption" color="text.secondary">Tarih</Typography><Typography>{formatDate(selectedTicket.ZAMAN)}</Typography></Grid>
                            <Grid item xs={12}><Typography variant="caption" color="text.secondary">Durum</Typography><Typography>{selectedTicket.DESTEK_DURUM_ADI}</Typography></Grid>
                            <Grid item xs={12}><Typography variant="caption" color="text.secondary">Başlık</Typography><Typography sx={{fontWeight:'bold'}}>{selectedTicket.DESTEK_BASLIK}</Typography></Grid>
                            <Grid item xs={12}><Typography variant="caption" color="text.secondary">Mesajınız</Typography><Paper variant="outlined" sx={{p:2, mt:0.5, whiteSpace:'pre-wrap'}}>{selectedTicket.DESTEK_ICERIK}</Paper></Grid>
                            {selectedTicket.CEVAP_ICERIK && <Grid item xs={12}><Typography variant="caption" color="text.secondary">Yetkili Cevabı</Typography><Paper variant="outlined" sx={{p:2, mt:0.5, bgcolor:'#f5f5f5', whiteSpace:'pre-wrap'}}>{selectedTicket.CEVAP_ICERIK}</Paper></Grid>}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions><Button onClick={closeModals} sx={{color: themeColor}}>Kapat</Button></DialogActions>
            </Dialog>

            <Dialog open={isNewOrEditModalOpen} onClose={closeModals} fullWidth maxWidth="sm">
                <DialogTitle>{selectedTicket ? 'Destek Talebini Düzenle' : 'Yeni Destek Talebi Oluştur'}<IconButton onClick={closeModals} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton></DialogTitle>
                <DialogContent>
                    {modalLoading ? <CircularProgress sx={{ color: themeColor }} /> :
                        <YeniDestekTalepForm 
                            onSubmit={handleFormSubmit} 
                            onCancel={closeModals}
                            themeColor={themeColor}
                            themeColorDark={themeColorDark}
                            initialValues={selectedTicket || {}}
                            durumlar={destekDurumlari}
                        />
                    }
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DestekTalepleri;